export const PROFESSIONAL_VIDEO_LANGUAGES = Object.freeze(["en", "es", "ht", "fr", "pt"]);

export const PROFESSIONAL_VIDEO_LANGUAGE_NAMES = Object.freeze({
  en: "English",
  es: "Español",
  ht: "Kreyòl Ayisyen",
  fr: "Français",
  pt: "Português"
});

const cleanHost = (hostname = "") => hostname.toLowerCase().replace(/^(www\.|m\.)/, "");

export const analyzeProfessionalVideoUrl = (value) => {
  const originalUrl = String(value || "").trim();
  if (!originalUrl) return null;

  let parsed;
  try {
    parsed = new URL(originalUrl);
  } catch {
    return { valid: false, reason: "invalid" };
  }
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) {
    return { valid: false, reason: "https" };
  }

  const host = cleanHost(parsed.hostname);
  const path = parsed.pathname;
  let match;

  if (host === "youtu.be" || host === "youtube.com" || host.endsWith(".youtube.com")) {
    const id = host === "youtu.be"
      ? path.split("/").filter(Boolean)[0]
      : (parsed.searchParams.get("v") || path.match(/^\/(?:embed|shorts)\/([A-Za-z0-9_-]{6,})/)?.[1]);
    if (id && /^[A-Za-z0-9_-]{6,}$/.test(id)) {
      return {
        valid: true,
        platform: "youtube",
        originalUrl: parsed.toString(),
        embedUrl: `https://www.youtube.com/embed/${id}`,
        vertical: path.startsWith("/shorts/")
      };
    }
  }

  if (host === "vimeo.com" || host.endsWith(".vimeo.com")) {
    match = path.match(/\/(?:video\/)?(\d+)/);
    if (match) return { valid: true, platform: "vimeo", originalUrl: parsed.toString(), embedUrl: `https://player.vimeo.com/video/${match[1]}`, vertical: false };
  }

  if (host === "tiktok.com" || host.endsWith(".tiktok.com")) {
    match = path.match(/\/video\/(\d+)/);
    if (match) return { valid: true, platform: "tiktok", originalUrl: parsed.toString(), embedUrl: `https://www.tiktok.com/player/v1/${match[1]}`, vertical: true };
  }

  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    match = path.match(/^\/(reel|reels|p)\/([A-Za-z0-9_-]+)/);
    if (match) {
      const mediaType = match[1] === "p" ? "p" : "reel";
      return { valid: true, platform: "instagram", originalUrl: parsed.toString(), embedUrl: `https://www.instagram.com/${mediaType}/${match[2]}/embed/`, vertical: mediaType === "reel" };
    }
  }

  if (host === "facebook.com" || host.endsWith(".facebook.com") || host === "fb.watch") {
    const supportedPath = /\/(?:reel\/|watch|videos?\/)|\/videos\//.test(path) || host === "fb.watch";
    if (supportedPath) {
      return {
        valid: true,
        platform: "facebook",
        originalUrl: parsed.toString(),
        embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(parsed.toString())}&show_text=false`,
        vertical: path.includes("/reel/")
      };
    }
  }

  return { valid: true, platform: "external", originalUrl: parsed.toString(), embedUrl: "", vertical: false };
};

export const configuredProfessionalVideos = (professionalVideos = {}) =>
  PROFESSIONAL_VIDEO_LANGUAGES.flatMap((language) => {
    const value = professionalVideos?.[language];
    const url = typeof value === "string" ? value : value?.url;
    const media = analyzeProfessionalVideoUrl(url);
    return media?.valid ? [{ language, ...media }] : [];
  });

export const selectProfessionalVideoLanguage = (professionalVideos, activeLanguage = "en") => {
  const available = configuredProfessionalVideos(professionalVideos);
  if (!available.length) return "";
  if (available.length === 1) return available[0].language;
  if (available.some((video) => video.language === activeLanguage)) return activeLanguage;
  const configuredDefault = String(professionalVideos?.defaultLanguage || "").toLowerCase();
  if (available.some((video) => video.language === configuredDefault)) return configuredDefault;
  return available[0].language;
};
